import { Controller } from "@hotwired/stimulus"
import { optOutAndClearDeviceForDeletion } from "./onesignal_device_controller"

/**
 * Account Deletion Controller
 *
 * Handles account deletion with proper push notification cleanup.
 *
 * ============================================================
 * CRITICAL: Push Notification Handling on Account Deletion
 * ============================================================
 * 
 * On account deletion, we MUST call optOutPush via optOutAndClearDeviceForDeletion().
 * This is one of the ONLY two cases where optOutPush is allowed:
 * 1. Account deletion (this controller)
 * 2. User explicitly disabling notifications in settings
 * 
 * Why optOutPush is REQUIRED for account deletion:
 * - The user account is being permanently destroyed
 * - We must stop sending notifications to this device for the deleted user
 * - This prevents orphan push subscriptions in OneSignal
 * - Unlike logout, there's no expectation of re-login with the same account
 * 
 * Why optOutPush is FORBIDDEN on logout (different behavior):
 * - Logout is temporary - user will log back in
 * - optOutPush permanently disables push until optInPush is called
 * - requestPushPermission() does NOT re-enable after optOut
 * - See logout_controller.js for the correct logout handling
 *
 * ============================================================
 *
 * Usage:
 *   <button data-controller="account-deletion"
 *           data-action="click->account-deletion#handleDeletion"
 *           data-account-deletion-confirm-message-value="Are you sure?"
 *           data-account-deletion-url-value="/users"
 *           data-account-deletion-method-value="delete">
 *     Delete Account
 *   </button>
 */
export default class extends Controller {
  static values = {
    confirmMessage: String,
    url: String,
    method: { type: String, default: "delete" }
  }

  /**
   * Handle account deletion click
   * Shows confirmation, opts out from push, then proceeds with deletion
   */
  async handleDeletion(event) {
    event.preventDefault()

    // Show confirmation dialog
    if (this.hasConfirmMessageValue && this.confirmMessageValue) {
      if (!confirm(this.confirmMessageValue)) {
        return
      }
    }

    // Disable button to prevent double-clicks
    const button = event.currentTarget
    const originalText = button.textContent
    button.disabled = true
    button.textContent = "..."

    try {
      // CRITICAL: Opt out from push notifications before account deletion
      // This is one of the ONLY cases where optOutPush is allowed
      // This must happen before the user record is destroyed
      await optOutAndClearDeviceForDeletion()
    } catch (error) {
      // Don't block deletion if opt-out fails
      console.error("[AccountDeletion] Push opt-out failed:", error)
    }

    try {
      // Proceed with account deletion
      await this.submitDeletion()
    } catch (error) {
      console.error("[AccountDeletion] Deletion failed:", error)
      // Re-enable button on error
      button.disabled = false
      button.textContent = originalText
    }
  }

  /**
   * Submit the account deletion request
   */
  async submitDeletion() {
    const form = document.createElement("form")
    form.method = "POST"
    form.action = this.urlValue

    // Add method override for DELETE
    const methodInput = document.createElement("input")
    methodInput.type = "hidden"
    methodInput.name = "_method"
    methodInput.value = this.methodValue
    form.appendChild(methodInput)

    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const csrfInput = document.createElement("input")
      csrfInput.type = "hidden"
      csrfInput.name = "authenticity_token"
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    document.body.appendChild(form)
    form.submit()
  }
}
